import { motionConfig, type MotionSettings } from './motion';

export interface Testimonial {
  name: string;
  quote: string;
  media: {
    type: 'image' | 'video';
    src: string;
    alt?: string;
  };
}
export interface CTA {
  heading: string;
  description: string;
  buttonText: string;
  href: string;
}

export interface InfoCardItem {
  title: string;
  description: string;
  href?: string;
  imageSrc?: string;
  imageAlt?: string;
}

export interface HowItWorksItem {
  title: string;
  description: string;
  icon: string;
}

export interface StepItem {
  title: string;
  description: string;
}

export interface EducationModule {
  title: string;
  subtitle?: string;
  items: string[];
  microCTA?: { label: string; href: string };
}

export interface ServicePageContent {
  hero: {
    heading: string;
    subheading: string;
    image: { src: string; alt: string };
    cta?: { label: string; href: string };
  };
  // Clarity: simple explanation and why it matters
  clarity?: {
    heading: string;
    paragraphs: string[];
  };
  benefits: {
    layout?: { columns: number };
    heading: string;
    intro: string;
    items: Array<{ title: string; description: string; icon: string; checklist?: string[]; variant?: 'callout'; colSpan?: number }>;
    variant?: 'gray-container';
  };
  process: {
    heading: string;
    content?: string;
    // Timeline expectations to build trust
    timeline?: string;
    steps: Array<{ title: string; description: string; icon?: string }>;
  };
  materials: {
    heading: string;
    items: Array<{
      title: string;
      description: string;
      imageSrc: string;
      imageAlt: string;
      width?: number;
      height?: number;
    }>;
  };
  education?: {
    heading: string;
    modules: EducationModule[];
  };
  gallery: {
    heading: string;
    items: Array<{ src: string; alt: string; caption: string }>;
    moreLink?: { serviceId: string; label: string };
  };
  testimonials: {
    heading: string;
    items: Testimonial[];
  };
  cta: {
    heading: string;
    buttonText: string;
    href: string;
    // Reassurance about service area or next-step notes
    reassurance?: string;
  };
  // How Do I Get Started section
  getStarted?: {
    heading: string;
    intro: string;
    steps: StepItem[];
    serviceArea: string;
    cta: { label: string; href: string; phone: string };
  };
}

export interface FeaturedProject {
  title: string;
  beforeSrc: string;
  beforeAlt: string;
  afterSrc: string;
  afterAlt: string;
  href: string;
}

export interface SiteConfig {
  leadIntake: {
    workflow: string;
  };

  // Company Information
  company: {
    name: string;
    shortName: string;
    legalName: string;
    tagline: string;
    description: string;
    location: string;
    founded?: string;
    website: string;
    email: string;
    phone?: string;
    copyrightYear?: number;
  };

  // Branding & Assets
  branding: {
    logo: {
      primary: string; // Main logo path
      dark: string; // Dark version for footer
      square: string; // Square version for favicon
      diagrams: string; // Diagrams version for navbar
    };
    favicon: string;
    heroBackground: {
      mobile: string;
      desktop: string;
    };
  };

  // Layout configuration
  layout: {
    maxWidth: {
      sm: string;
      md: string;
      lg: string;
      xl: string;
      xxl: string;
    };
    spacing: {
      sectionY: string;
      stack: {
        sm: string;
        md: string;
        lg: string;
        xl: string;
        fluid: string;
      };
      padX: {
        sm: string;
        md: string;
        lg: string;
        xl: string;
        fluid: string;
      };
    };
  };

  // UI component defaults
  ui: {
    sectionMinHeight: string;
    viewGap: string;
    serviceFeatureMinHeight: string;
    serviceIconSize?: string;
    beforeAfterSlider: {
      beforeLabel: string;
      afterLabel: string;
    };
    testimonialsSection?: {
      background?: string;
    };
    projectNavigation: {
      enabled: boolean;
      wrapAround: boolean;
    };
    projectSections?: {
      designSpecBackground?: string;
    };
    ctaTextAlign?: 'left' | 'center' | 'right';
  };

  // Navigation
  navigation: {
    main: Array<{
      label: string;
      href: string;
      enabled: boolean;
      items?: Array<{ label: string; href: string }>;
    }>;
    logoSplitIndex?: number;
    bannerOffsetClass?: string;
    styling?: {
      allCaps?: boolean;
      fontWeight?: string;
      letterSpacing?: string;
      activeClass?: string;
    };
    mobileBackground?: string;
    footer: {
      enabled: boolean;
      showSocialLinks: boolean;
      showLocation: boolean;
      showMainNav: boolean;
      showServices: boolean;
    };
    navBarOffset: {
      mobile: string;
      desktop: string;
    };
  };

  // Top Banner Configuration
  banner?: {
    enabled: boolean;
  };

  // Social Media
  social: {
    facebook?: string;
    linkedin?: string;
    twitter?: string;
    instagram?: string;
    github?: string;
    youtube?: string;
    tiktok?: string;
    angieslist?: string;
    googleReviews?: string;
  };

  // SEO & Metadata
  seo: {
    title: {
      default: string;
      template: string;
    };
    description: string;
    keywords: string[];
    author: string;
    metadataBase: string;
    openGraph: {
      title: string;
      description: string;
      images: Array<{
        url: string;
        width: number;
        height: number;
        alt: string;
      }>;
    };
    twitter: {
      card: string;
      title: string;
      description: string;
      images: string[];
      creator: string;
    };
  };

  // Theme Configuration
  theme: {
    colors: {
      primary: string;
      primaryForeground: string;
      secondary: string;
      secondaryForeground: string;
      accent: string;
      background: string;
      foreground: string;
      muted: string;
      mutedForeground: string;
      border: string;
      input: string;
      ring: string;
      destructive: string;
      card: string;
      cardForeground: string;
      popover: string;
      popoverForeground: string;
    };
    borderRadius: {
      sm: string;
      md: string;
      lg: string;
      xl: string;
      button: string;
    };
  };

  // Font Configuration
  fonts: {
    heading: {
      family: string;
      googleFont: string;
      fallbacks: string[];
    };
    body: {
      family: string;
      googleFont: string;
      fallbacks: string[];
    };
  };

  // Motion/Animation Configuration
  motion: MotionSettings;

  // Content Configuration
  content: {
    hero: {
      title: string;
      subtitle: string;
      ctaText: string;
      ctaLink: string;
    };
    tagline: string;
    bannerText: string;
    introParagraphs: string[];
    about: {
      heroImage: {
        src: string;
        alt: string;
      };
      story: {
        heading: string;
        paragraph: string;
      };
      missionValues: Array<{
        title: string;
        description: string;
      }>;
      equipmentImages: Array<{
        src: string;
        alt: string;
        title: string;
        description: string;
      }>;
      teamImage: {
        src: string;
        alt: string;
      };
      team: {
        heading: string;
        paragraph: string;
        reasons: string[];
      };
      whySelectTrak: Array<{
        title: string;
        description: string;
      }>;
      executiveBio: {
        name: string;
        title: string;
        image: string;
        bio: string[];
      };
      clientTestimonial: {
        quote: string;
        author?: string;
      };
    };
    contactPage: {
      heading: string;
      subheading: string;
      feeObligation: string;
    };
    contactForm: {
      nameLabel: string;
      namePlaceholder: string;
      emailLabel: string;
      emailPlaceholder: string;
      phoneLabel: string;
      phonePlaceholder: string;
      messageLabel: string;
      messagePlaceholder: string;
      messageDefault: string;
      submitText: string;
      helperText: string;
    };
    ctaImage: {
      src: string;
      alt: string;
    };
    ctas: {
      home: CTA;
      services: CTA;
      projects: CTA;
      about: CTA;
      contact: CTA;
    };
    getStarted: {
      heading: string;
      intro: string;
      steps: StepItem[];
      serviceArea: string;
      cta: { label: string; href: string; phone: string };
    };
    featuredProject: FeaturedProject;
    howItWorks: HowItWorksItem[];
    servicesSnapshot: InfoCardItem[];
    whyChooseUs: InfoCardItem[];
    gallery: {
      heading: string;
      description?: string;
      imageDir: string;
      tagFile: string;
      searchPlaceholder: string;
      filterTags: Array<'service' | 'material' | 'features'>;
      serviceLabels?: Record<string, string>;
      defaultService?: string;
    };
    techLogos: Array<{ src: string; alt: string }>;
    whyTrakImage: string;
    aboutSummaryImage: string;
  };

  // Services Configuration
  services: Array<{
    title: string;
    slug: string;
    description: string;
    longDescription: string;
    imageSrc: string;
    imagePosition?: 'left' | 'right';
    icon: string;
    cta: string;
    href: string;
    enabled: boolean;
    heroImage: { src: string; alt: string };
    images: Array<{ src: string; alt: string }>;
    page?: ServicePageContent;
    iconImageSrc?: string;
  }>;

  // FAQ Configuration
  faqs: {
    services: Array<{
      question: string;
      answer: string;
    }>;
  };

  // Projects
  projects: Array<{
    slug: string;
    label: string;
    description: string;
    imageSrc: string;
    heroImage: string;
    gallery: string[];
    category: string;
    enabled: boolean;
    background?: string;
    problem?: string;
    solution?: string;
    vision?: {
      text: string;
      image: string | null;
      objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
    };
    design?: {
      text: string;
      image: string | null;
      objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
    };
    build?: {
      text: string;
      image: string | null;
      objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
    };
    beforeAfter?: {
      before: string | null;
      after: string | null;
      objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
    };
  }>;

  // Testimonials
  testimonials?: Testimonial[];

  // Contact Information
  contact: {
    address?: string;
    hours?: string;
    emergencyContact?: string;
  };
}

// Site Configuration - Edit this to customize for your company
export const siteConfig: SiteConfig = {
  leadIntake: {
    workflow: 'trak_communications.contact_request.v1',
  },

  company: {
    name: "TRAK Communications, Inc.",
    shortName: "TRAK Communications",
    legalName: "TRAK Communications, Inc.",
    tagline: "CONTROL TELECOM COSTS with expert audit services",
    description:
      "TRAK Communications has been helping its clients save money for over 25 years. Privately held, woman owned small business.",
    location: "NJ",
    founded: "1999", // implied 25+ years
    website: process.env.NEXT_PUBLIC_SITE_URL || "https://www.trakcommunications.com",
    email: "rsmaldone@trakcommunications.com",
    phone: "(732) 786-1355",
    copyrightYear: 2026,
  },

  branding: {
    logo: {
      primary: "/images/logo.svg",
      dark: "/images/logo.svg",
      square: "/images/logo.svg",
      diagrams: "/images/logo.svg",
    },
    favicon: "/logo",
    heroBackground: {
      mobile: "/images/hero-bckg",
      desktop: "/images/hero-bckg",
    },
  },

  layout: {
    maxWidth: {
      sm: '40rem',
      md: '48rem',
      lg: '64rem',
      xl: '80rem',
      xxl: '90rem',
    },
    spacing: {
      sectionY: 'clamp(3rem, 8vw, 6rem)',
      stack: {
        sm: '0.5rem',
        md: '1rem',
        lg: '1.5rem',
        xl: '2rem',
        fluid: 'clamp(1rem, 2vw, 3rem)',
      },
      padX: {
        sm: '1rem',
        md: 'clamp(1rem, 4vw, 3rem)',
        lg: '5rem',
        fluid: 'clamp(1rem, 5vw, 6rem)',
        xl: '8rem',
      },
    },
  },

  ui: {
    sectionMinHeight: '66vh',
    viewGap: 'gap-12',
    serviceFeatureMinHeight: '500px',
    serviceIconSize: 'h-10 w-10',
    beforeAfterSlider: {
      beforeLabel: "Before",
      afterLabel: "After",
    },
    testimonialsSection: {
      background: 'bg-gray-100',
    },
    projectNavigation: {
      enabled: false,
      wrapAround: false,
    },
    projectSections: {
      designSpecBackground: 'bg-muted',
    },
    ctaTextAlign: 'center',
  },

  navigation: {
    main: [
      {
        label: "Home",
        href: "/",
        enabled: true,
      },
      {
        label: "Services",
        href: "/services",
        enabled: true,
        items: [
          { label: "Telecom Audit", href: "/services/telecom-audit" },
          { label: "Cost Savings Analysis", href: "/services/cost-saving-analysis" },
          { label: "Physical Validation & Inventory", href: "/services/physical-validation-inventory-services" },
          { label: "Advisory Services", href: "/services/advisory-services" }
        ]
      },
      {
        label: "About",
        href: "/about-us",
        enabled: true,
      },
      {
        label: "Contact",
        href: "/contact",
        enabled: true,
      },
    ],
    logoSplitIndex: 2,
    bannerOffsetClass: 'top-8 md:top-9',
    styling: {
      allCaps: true,
      fontWeight: "font-medium",
      letterSpacing: "tracking-wide",
      activeClass: "text-secondary",
    },
    mobileBackground: "#ffffff",
    footer: {
      enabled: true,
      showSocialLinks: true,
      showLocation: true,
      showMainNav: true,
      showServices: true,
    },
    navBarOffset: {
      mobile: '72px',
      desktop: '180px',
    },
  },

  banner: {
    enabled: false,
  },

  social: {
    linkedin: "https://www.linkedin.com/company/trak-communications",
    twitter: "https://x.com/TrakComm",
    instagram: "https://www.instagram.com/trakcomm",
  },

  seo: {
    title: {
      default:
        "Telecom Audit & Cost Reduction Services | TRAK Communications",
      template: "%s | TRAK Communications",
    },
    description:
      "Control telecom costs with expert audit services. TRAK Communications reduces 20-30% of your ongoing costs and puts money back in your budget.",
    keywords: [
      "telecom audit",
      "cost reduction",
      "network optimization",
      "telecom expense management",
      "contract negotiation",
    ],
    author: "TRAK Communications, Inc.",
    metadataBase: "https://www.trakcommunications.com",
    openGraph: {
      title: "Telecom Audit & Cost Reduction Services | TRAK Communications",
      description:
        "Control telecom costs with expert audit services. TRAK Communications reduces 20-30% of your ongoing costs and puts money back in your budget.",
      images: [
        {
          url: "/images/TRAK_Logo-Web.png",
          width: 1200,
          height: 630,
          alt: "TRAK Communications",
        },
      ],
    },
    twitter: {
      card: "summary_large_image",
      title: "Telecom Audit & Cost Reduction Services | TRAK Communications",
      description:
        "Control telecom costs with expert audit services. TRAK Communications reduces 20-30% of your ongoing costs and puts money back in your budget.",
      images: ["/images/TRAK_Logo-Web.png"],
      creator: "@TrakComm",
    },
  },

  theme: {
    colors: {
      primary: "#391d5a",
      primaryForeground: "#ffffff",
      secondary: "#8dc63f",
      secondaryForeground: "#ffffff",
      accent: "#8dc63f",
      background: "#ffffff",
      foreground: "#0f172a",
      muted: "#f1f5f9",
      mutedForeground: "#64748b",
      border: "#e2e8f0",
      input: "#e2e8f0",
      ring: "#391d5a",
      destructive: "#dc3545",
      card: "#ffffff",
      cardForeground: "#0f172a",
      popover: "#ffffff",
      popoverForeground: "#0f172a",
    },
    borderRadius: {
      sm: "0.25rem",
      md: "0.375rem",
      lg: "0.5rem",
      xl: "0.75rem",
      button: "0.5rem"
    },
  },

  fonts: {
    heading: {
      family: "Bodoni Moda",
      googleFont: "Bodoni_Moda:wght@400;500;600;700;800",
      fallbacks: ["Georgia", "serif"]
    },
    body: {
      family: "Karla",
      googleFont: "Karla:wght@300;400;500;600;700",
      fallbacks: ["ui-sans-serif", "system-ui", "sans-serif"]
    }
  },
  motion: motionConfig,

  content: {
    hero: {
      title: "Comprehensive Business Audits for Maximum Cost Savings",
      subtitle:
        "Let us reduce 20-30% of your ongoing costs and put money back in your budget",
      ctaText: "Schedule a free consultation",
      ctaLink: "/contact",
    },
    tagline:
      "Reduce Costs by up to 30%",
    bannerText: 'Reduce Costs by up to 30%',
    introParagraphs: [
      "Privately held, woman owned small business.",
      "Unbiased and vendor neutral.",
      "Highly experienced (25+ years) team members.",
      "We have a reputation of excellence with industry vendors and carriers."
    ],
    about: {
      heroImage: {
        src: "/images/hero-bckg",
        alt: "Team working on telecom audit",
      },
      story: {
        heading: "Our Story",
        paragraph:
          "TRAK Communications, Inc. is a woman‑owned, privately held telecommunications auditing firm. Clients benefit from our excellent reputation and strong relationships with telecom carriers. We have been helping clients save money for over 25 years.",
      },
      missionValues: [
        {
          title: "100% Unbiased",
          description:
            "We are not affiliated with any carriers; our recommendations are 100% unbiased.",
        },
        {
          title: "Expert Team",
          description:
            "Highly experienced (25+ years) team members. We use our own technicians to perform physical inventories.",
        },
        {
          title: "Agile Service",
          description:
            "As a boutique firm we adapt easily, rivaling the turnaround time of larger competitors.",
        },
        {
          title: "Integrated approach",
          description:
            "We integrate our expert staff with your internal resources to deliver savings.",
        },
      ],
      equipmentImages: [],
      teamImage: {
        src: "/images/laptop-hands",
        alt: "TRAK Communications Team",
      },

      whySelectTrak: [
        {
          title: "100% Independent",
          description: "Of the service providers, guaranteeing objectivity across all aspects of service evaluations."
        },
        {
          title: "Highly Experienced Staff",
          description: "Each of TRAK’s analysts has over 25 years of experience!"
        },
        {
          title: "Faster Results",
          description: "Our excellent relationships with vendors and carriers enables us to proceed efficiently and obtain information efficiently."
        },
        {
          title: "Expertise",
          description: "Advisor and Lead Analyst for the industry’s leading telecommunications bill audit firms."
        },
        {
          title: "Team Players",
          description: "We become part of your team; whereas our competitors are involved in a factory setting and your audit is one of many."
        },
        {
          title: "Streamlined Processes",
          description: "Thorough and well-documented, we work efficiently to authenticate every billing issue and provide informative status reports."
        }
      ],
      executiveBio: {
        name: "Randi Smaldone",
        title: "President",
        image: "/images/randi-smaldone-profile-pic",
        bio: [
          "Highly Experienced IT Executive with Consultant Background and over 30 years experience conducting rate and tariff analysis for voice, data and wireless services.",
          "Proven track record for providing high level and technical cost reduction analysis to significantly reduce telecommunications expenses and improve quality of services.",
          "Developed and managed audit practices for several firms. Served as Director of Audit Operations for two of the largest and well known TEM companies.",
          "Provide management and support to several Telecom Expense Management firms with the internal auditing of their clients."
        ]
      },
      clientTestimonial: {
        quote: "TRAK Communications has been a KGM Consulting partner for over 10 years. TRAK has helped KGM save our clients millions of dollars in cost reductions and project savings that have resulted from the detailed audits and physical validations that TRAK provides. TRAK professional staff are experts in saving our customers money and time. Examples include finding billing errors, disconnecting services not in use, and negotiating better contract rates. I highly recommend TRAK Communications.",
        author: "Client Testimonial"
      },
      team: {
        heading: "Our Professional Highlights",
        paragraph:
          "Our team delivers tangible results.",
        reasons: [
          "Recent audits have resulted in the delivery of <span class='text-[#8dc63f] font-bold'>$10 million</span> in savings for a global financial company",
          "Audit of a leading provider of global brokerage services resulted in an overall <span class='text-[#8dc63f] font-bold'>37%</span> annual savings",
          "Managed an RFP Process for a Public Utility Company that reduced the spend by <span class='text-[#8dc63f] font-bold'>32%</span>",
          "Several client refunds have amounted to more than <span class='text-[#8dc63f] font-bold'>$3 million</span> each"
        ],
      },
    },
    contactPage: {
      heading: 'Contact Us',
      subheading: "Want to know how much you’ll save? Contact us for a free consultation!",
      feeObligation: "We place no financial obligation on your company. Our fees are based on a percentage of the refunds or savings you receive.",
    },
    contactForm: {
      nameLabel: 'Full Name',
      namePlaceholder: 'Jane Doe',
      emailLabel: 'Email',
      emailPlaceholder: 'jane@company.com',
      phoneLabel: 'Phone',
      phonePlaceholder: '(555) 123-4567',
      messageLabel: 'Message',
      messagePlaceholder: 'Tell us about your current communications setup...',
      messageDefault: '',
      submitText: 'Submit Request',
      helperText: 'We will respond within one business day.',
    },
    ctaImage: {
      src: '/images/contact-cta.webp',
      alt: 'Contact us for savings',
    },
    ctas: {
      home: {
        heading: 'Ready to start saving?',
        description:
          "Contact us today for a complimentary consultation.",
        buttonText: 'Get Started',
        href: '/contact',
      },
      services: {
        heading: 'Our Services',
        description:
          'We offer a suite of services designed to optimize your infrastructure and reduce costs.',
        buttonText: 'View Services',
        href: '/services',
      },
      projects: {
        heading: 'See Our Results',
        description:
          'Learn how we have saved millions for our clients.',
        buttonText: 'About Us',
        href: '/about-us',
      },
      about: {
        heading: 'Ready to Start Saving?',
        description:
          'Contact us today to see how much you can save.',
        href: '/contact',
        buttonText: 'Contact Us',
      },
      contact: {
        heading: 'Contact Us Today',
        description:
          'Send us a message or give us a call.',
        buttonText: 'Back to Home',
        href: '/',
      },
    },
    getStarted: {
      heading: 'How Do I Get Started?',
      intro:
        "It's simple to start saving. Contact us and we will handle the rest.",
      steps: [
        { title: 'Step 1', description: 'Contact us for a consultation.' },
        {
          title: 'Step 2',
          description: "We analyze your telecom spend and contracts.",
        },
        {
          title: 'Step 3',
          description: "You start saving money.",
        },
      ],
      serviceArea:
        'Serving clients nationwide.',
      cta: {
        label: 'Contact Us Today',
        href: '/contact',
        phone: '(732) 786-1355',
      },
    },
    featuredProject: {
      title: 'Global Financial Company Audit',
      beforeSrc: '/images/audit-before.webp',
      beforeAlt: 'Before Audit',
      afterSrc: '/images/audit-after.webp',
      afterAlt: 'Delivered $10 million in savings',
      href: '/about-us',
    },
    howItWorks: [
      {
        title: 'Audit',
        description: 'Comprehensive review of your contracts and tariffs.',
        icon: 'consult',
      },
      {
        title: 'Analyze',
        description: 'Identify cost-reduction opportunities.',
        icon: 'design',
      },
      {
        title: 'Optimize',
        description: 'Negotiate and implement savings.',
        icon: 'build',
      },
    ],
    servicesSnapshot: [
      {
        title: 'Telecom Audit',
        description: 'Audit includes contract compliance, tariff review, frame relay optimization and more.',
        href: '/services/telecom-audit',
      },
      {
        title: 'Cost Analysis',
        description: 'Services include contract negotiation, network optimization, and rate optimization.',
        href: '/services/cost-analysis',
      },
      {
        title: 'Physical Validation',
        description: 'On-site investigations ensure you are only paying for services that are in place.',
        href: '/services/physical-validation',
      },
    ],
    whyChooseUs: [
      {
        title: 'Woman Owned',
        description: 'Privately held, woman-owned business committed to excellence and integrity.',
        imageSrc: '/images/woman-owned.webp',
        imageAlt: 'Woman owned business',
      },
      {
        title: 'Unbiased & Vendor Neutral',
        description:
          "Not affiliated with any carriers—we work solely in your best interest.",
        imageSrc: '/images/unbiased.webp',
        imageAlt: 'Unbiased advice',
      },
      {
        title: '25+ Years of Experience',
        description:
          "Over two decades of expertise in telecommunications cost management.",
        imageSrc: '/images/experienced.webp',
        imageAlt: 'Experienced team',
      },
      {
        title: 'Highly Reputable',
        description:
          "Trusted and respected by industry vendors and carriers nationwide.",
        imageSrc: '/images/responsive.webp',
        imageAlt: 'Reputable company',
      },
    ],
    gallery: {
      heading: 'Our Work',
      description: 'See our results.',
      imageDir: '/images/gallery',
      tagFile: 'gallery-tags.json',
      searchPlaceholder: 'Search...',
      filterTags: ['service', 'features'], // "material" not relevant
      serviceLabels: {
        'telecom-audit': 'Telecom Audit',
        'cost-analysis': 'Cost Analysis',
      },
      defaultService: 'telecom-audit',
    },
    techLogos: [],
    whyTrakImage: "/images/analytical",
    aboutSummaryImage: "/images/analytical",
  },

  services: [
    {
      title: "Telecom Audit",
      slug: "telecom-audit",
      description: "Audit includes contract compliance, tariff review, and more.",
      longDescription: "Our audit service can be one-time or ongoing. Audit areas include Wireless Plan Optimization, Physical Verification, Network Optimization, Broker Line Validation, Tax and Surcharge Review, Benchmarking, and Cost Savings Analysis.",
      imageSrc: "/icons/magnifier.svg",
      icon: "phone",
      iconImageSrc: "/icons/magnifier.svg",
      cta: "Get an Audit",
      href: "/services/telecom-audit",
      enabled: true,
      heroImage: { src: "/images/rate-audit-image", alt: "Telecom Audit" },
      images: [],
      page: {
        hero: {
          heading: "Telecom Audit",
          subheading: "Comprehensive analysis of your telecommunications infrastructure.",
          image: { src: "/images/rate-audit-image", alt: "Telecom Audit" }
        },
        benefits: {
          layout: { columns: 2 },
          heading: "What's Included",
          intro: "Our reputation depends on your satisfaction – we strive to deliver perfection!",
          items: [
            {
              title: "Communications Bill Auditing",
              description: "(Voice, Data, Internet, VoIP, Wireless)",
              icon: "file-text",
              colSpan: 2,
              checklist: [
                "Historical Refund Audits (rate, tariff, contract compliance)",
                "One Time or On-going Wireless Plan Optimization",
                "Physical Verification",
                "Network Optimization",
                "Broker Line Validation",
                "Tax & Surcharge Review",
                "Benchmarking"
              ]
            },
            {
              title: "Cost Savings Analysis & Network Optimization",
              description: "",
              icon: "trending-up",
              checklist: [
                "Contract Negotiations",
                "Network Optimization",
                "Rate Optimization"
              ]
            },
            {
              title: "Physical Validation",
              description: "",
              icon: "clipboard-list",
              checklist: [
                "Inventory Builds",
                "Site Surveys",
                "Service Validations"
              ]
            }
          ]
        },
        process: {
          heading: "Contract Negotiations",
          content: "We handle contract negotiations through a streamlined RFP Process, ensuring you get the best rates and terms available.",
          steps: []
        },
        materials: {
          heading: "",
          items: [
            {
              title: "",
              description: "",
              imageSrc: "/images/rate-audit-image",
              imageAlt: "Rate Audit Diagram",
              width: 623,
              height: 293
            }
          ]
        },
        gallery: { heading: "Gallery", items: [] },
        testimonials: { heading: "Testimonials", items: [] },
        cta: { heading: "Ready to audit?", buttonText: "Get Started", href: "/contact" }
      }
    },
    {
      title: "Cost Savings Analysis",
      slug: "cost-saving-analysis",
      description: "Services include contract negotiation, network optimization, and rate optimization.",
      longDescription: "Our commitment is to review ways to lower your monthly expenditures without affecting the quality of your service. Implementation is your option. Our assessment is always impartial and objective.",
      imageSrc: "/icons/geardoc.svg",
      icon: "chart-bar",
      iconImageSrc: "/icons/geardoc.svg",
      cta: "Start Saving",
      href: "/services/cost-saving-analysis",
      enabled: true,
      heroImage: { src: "/images/cost-savings-analysis-image", alt: "Cost Savings" },
      images: [],
      page: {
        hero: {
          heading: "Cost Savings Analysis",
          subheading: "Lower your monthly expenditures without affecting quality.",
          image: { src: "/images/cost-savings-analysis-image", alt: "Cost Savings" }
        },
        benefits: {
          heading: "What's Included",
          intro: "Our assessment is always impartial and objective – we are not affiliated with any carriers and do not accept commissions from them. Implementation is your option! Our commitment is to review ways to lower your monthly expenditures without affecting the quality of your service.",
          items: [
            {
              title: "Contract Negotiations",
              description: "We’ve developed a quick and efficient RFP process that has proven to save our clients 20-30%.",
              icon: "file-text"
            },
            {
              title: "Network Optimization",
              description: "We seek to eliminate duplications in the network design, identify underutilized circuit bandwidth, and remove unused services.",
              icon: "trending-up"
            },
            {
              title: "Rate Optimization",
              description: "Analysis of your current rates to ensure you are receiving the most competitive pricing available for your specific services and volume.",
              icon: "chart-bar"
            },
            {
              title: "Wireless Plan Optimization",
              description: "Analyze calling plans and features as well as identifying devices with zero usage for 3-6 months, or users that are no longer employed.",
              icon: "phone"
            },
          ]
        },
        process: { heading: "Our Process", steps: [] },
        materials: {
          heading: "",
          items: [
            {
              title: "",
              description: "",
              imageSrc: "/images/cost-savings-analysis-image",
              imageAlt: "Cost Savings Analysis Diagram",
              width: 623,
              height: 293
            }
          ]
        },
        gallery: { heading: "Gallery", items: [] },
        testimonials: { heading: "Testimonials", items: [] },
        cta: { heading: "Start saving today", buttonText: "Get Started", href: "/contact" }
      }
    },
    {
      title: "Physical Validation & Inventory Services",
      slug: "physical-validation-inventory-services",
      description: "We verify that every service billed is a working, valid service.",
      longDescription: "Technicians are sent on-site to conduct a physical inspection of carrier demarcation points. A non-invasive test is conducted on every line and circuit on site. Findings are compared against the billing inventory.",
      imageSrc: "/icons/serverchecklist.svg",
      icon: "clipboard-list",
      iconImageSrc: "/icons/serverchecklist.svg",
      cta: "Verify Assets",
      href: "/services/physical-validation-inventory-services",
      enabled: true,
      heroImage: { src: "/images/physical-validation-image", alt: "Physical Validation" },
      images: [],
      page: {
        hero: {
          heading: "Physical Validation & Inventory Services",
          subheading: "We verify that every service billed is a working, valid service.",
          image: { src: "/images/physical-validation-image", alt: "Validation" }
        },
        benefits: {
          heading: "Service Benefits",
          intro: "As part of our audit we verify that every service billed is a working, valid service.",
          items: [
            {
              title: "Dispute Resolution",
              description: "Any lines or services that are found to be inoperable are issued as a dispute to the appropriate billing carrier and a refund is negotiated when possible.",
              icon: "file-text"
            },
            {
              title: "Cost Savings Opportunities",
              description: "Lines or services that are found working but not used (no cross connects to equipment) are cost savings opportunities.",
              icon: "trending-up"
            },
            {
              title: "Proven Results",
              description: "Historically, our physical validation & inventory services result in 50% of the overall savings to our clients. Developing an accurate inventory is integral to receiving the cost savings for your audit.",
              icon: "chart-bar"
            },
            {
              title: "Complete Inventory",
              description: "At the end of the audit we will provide you with a complete inventory of your services at no extra cost – for you to manage going forward.",
              icon: "clipboard-list"
            },
          ]
        },
        process: { heading: "Our Process", steps: [] },
        materials: {
          heading: "",
          items: [
            {
              title: "",
              description: "",
              imageSrc: "/images/physical-validation-image",
              imageAlt: "Physical Validation Diagram",
              width: 623,
              height: 293
            }
          ]
        },
        gallery: { heading: "Gallery", items: [] },
        testimonials: { heading: "Testimonials", items: [] },
        cta: { heading: "Validate your inventory", buttonText: "Get Started", href: "/contact" }
      }
    },
    {
      title: "Advisory Services",
      slug: "advisory-services",
      description: "With over 30 years of experience in the telecommunications industry, we have built strong alliances with numerous Telecom Expense Management (TEM) companies.",
      longDescription: "Our Advisory Services Cover a Wide Range of Telecom Needs Including TEM Selection and Implementation, Mergers and Acquisitions Billing Management, Telecom Audit, and Process Improvement.",
      imageSrc: "/icons/check.svg",
      icon: "advisory",
      iconImageSrc: "/icons/check.svg",
      cta: "Learn More",
      href: "/services/advisory-services",
      enabled: true,
      heroImage: { src: "/images/laptop-hands", alt: "Advisory Services" }, // Reusing image
      images: [],
      page: {
        hero: {
          heading: "Advisory Services",
          subheading: "",
          image: { src: "/images/laptop-hands", alt: "Advisory Services" }
        },
        materials: {
          heading: "",
          items: [
            {
              title: "",
              description: "",
              imageSrc: "/images/laptop-hands",
              imageAlt: "Advisory Services",
              width: 1536,
              height: 1024
            }
          ]
        },
        benefits: {
          layout: { columns: 3 },
          heading: "",
          intro: "With over 30 years of experience in the telecommunications industry, we have built strong alliances with numerous Telecom Expense Management (TEM) companies.",
          items: [
            {
              title: "TEM System selection & Implementation",
              description: "Retained by clients that require expert project management for the end to end solution",
              icon: "clipboard-list"
            },
            {
              title: "Outsourced auditing partnerships",
              description: "Engaged by TEM companies to serve as a strategic partner for auditing services",
              icon: "users"
            },
            {
              title: "Acquisitions and Mergers",
              description: "Manage telecom merger and acquisition cost strategies across companies and align contracts, invoicing and services.",
              icon: "trending-up"
            },
          ]
        },
        process: { heading: "Our Process", steps: [] },
        gallery: { heading: "Gallery", items: [] },
        testimonials: { heading: "Testimonials", items: [] },
        cta: { heading: "Contact Us", buttonText: "Get Started", href: "/contact" }
      }
    },
    // Disabled / Legacy services
    {
      title: "Market Data Assessment",
      slug: "market-data-assessment",
      description: "Comparative analysis of your current rates against market benchmarks to ensure you're getting competitive pricing for your services.",
      longDescription: "",
      imageSrc: "/images/icons/trending-up.svg",
      icon: "trending-up",
      cta: "Compare Rates",
      href: "/services/market-data-assessment",
      enabled: false,
      heroImage: { src: "/images/services/market-hero.webp", alt: "Market Assessment" },
      images: [],
      // @ts-ignore
      page: {}
    },
    {
      title: "Vendor Management",
      slug: "vendor-management",
      description: "Ongoing support with vendor relations, dispute resolution, and ensuring service level agreements are met by your providers.",
      longDescription: "",
      imageSrc: "/images/icons/users.svg",
      icon: "users",
      cta: "Manage Vendors",
      href: "/services/vendor-management",
      enabled: false,
      heroImage: { src: "/images/services/vendor-hero.webp", alt: "Vendor Management" },
      images: [],
      // @ts-ignore
      page: {}
    },
    {
      title: "Contract Review & Analysis",
      slug: "contract-review-analysis",
      description: "Thorough examination of service agreements and vendor contracts to identify unfavorable terms and renegotiation opportunities.",
      longDescription: "",
      imageSrc: "/images/icons/file-text.svg",
      icon: "file-text",
      cta: "Review Contracts",
      href: "/services/contract-review-analysis",
      enabled: false,
      heroImage: { src: "/images/services/contract-hero.webp", alt: "Contract Review" },
      images: [],
      // @ts-ignore
      page: {}
    }
  ],

  faqs: {
    services: [],
  },

  projects: [], // No projects provided

  testimonials: [
    {
      name: "KGM, President – KGM Consulting, Inc.",
      quote: "TRAK Communications has been a KGM Consulting partner for over 10 years. They have helped KGM save our clients millions of dollars...",
      media: { type: 'image', src: '/images/logo-2x.webp', alt: 'Testimonial' },
    },
    {
      name: "MC, one of the world’s leading financial groups based in Japan",
      quote: "I have personally worked with TRAK Communications for 15 years over two different companies... Her company is a true business partner...",
      media: { type: 'image', src: '/images/logo-2x.webp', alt: 'Testimonial' },
    }
  ],

  contact: {
    address: "",
    hours: '9am - 5pm EST',
    emergencyContact: '(732) 786-1355',
  },
}
